







import { Component, Vue, Prop, Ref, Watch } from "vue-property-decorator";
import Chart from "chart.js";
import * as ChartGeo from "chartjs-chart-geo/build/Chart.Geo.esm";
import Color from "color";
import axios from "axios";
import ukr from "../assets/data/ukr.topo.json";
import translations from "../assets/data/regions.json";
import { max, sum, min } from "lodash";

@Component
export default class BiMap extends Vue {
  @Ref("canvas") canvas!: HTMLCanvasElement;
  @Prop() title!: string;
  @Prop({ default: [] }) data!: { [key: string]: number | string };
  @Prop({ type: Boolean, default: false }) withPercent!: boolean;
  chart: Chart | null = null;
  @Prop({ type: Boolean, default: false }) isBlue!: boolean;
  @Watch("data")
  onData() {
    if (this.data) {
      this.initMap();
    }
  }

  strToNumber(value: any) {
    if (typeof value !== "string") {
      return value;
    }

    return Number(
      value
        .split("")
        .map((letter: any) => {
          if (!isNaN(letter) || letter === ".") {
            return letter;
          }

          return "";
        })
        .join("")
    );
  }

  percentageData(data: Object) {
    const geoValues = Object.values(data);
    const totalValues = geoValues.reduce((acc: number, item: any) => {
      return (acc += item);
    }, 0);

    return Object.entries(data).reduce((acc, [key, value]: any) => {
      acc[key] = `${((value * 100) / totalValues).toFixed(2)}%`;
      return acc;
    }, {} as any);
  }

  async initMap() {
    const nation = ChartGeo.topojson.feature(ukr, ukr.objects.ukr).features;
    const labels = ukr.objects.ukr.geometries.map((item: any) => item.properties.name);

    const data = this.withPercent
      ? Object.entries(this.percentageData(this.data))
      : Object.entries(this.data);

    const findValue = (feature: any) => {
      const name = feature.properties.name.toLowerCase();
      const value = data.find(([key]) => key.toLowerCase().includes(name));

      if (!value) {
        return 0;
      }
      return value[1];
    };

    const color = this.isBlue ? "#429ADA" : "#D7E8C7";

    this.chart = new Chart(this.canvas, {
      type: "choropleth",
      data: {
        labels,
        datasets: [
          {
            outline: nation, // ... outline to compute bounds
            showOutline: true,
            hoverBackgroundColor: "#647272",
            backgroundColor: (context: any) => {
              if (context.dataIndex == null) {
                return null;
              }

              /** Using min/max normalization here */
              const MIN_OPACITY = 0.7;
              const VALUE_MULTIPLIER = 3;
              const value = context.dataset.data[context.dataIndex];
              const maxValue = max(
                context.dataset.data.map((v: any) => this.strToNumber(v.value))
              ) as number;
              const minValue = min(
                context.dataset.data.map((v: any) => this.strToNumber(v.value))
              ) as number;
              if (maxValue === 0) {
                return new Color(color)
                  .darken(0.25)
                  .fade(0.7)
                  .rgb()
                  .string();
              }
              let numberVal = this.withPercent ? this.strToNumber(value.value) : value.value;
              let valuePosition = (numberVal - minValue) / (maxValue - minValue);
              valuePosition =
                valuePosition === 1
                  ? 0
                  : valuePosition === 0
                  ? MIN_OPACITY
                  : MIN_OPACITY - valuePosition * VALUE_MULTIPLIER;
              return new Color(color)
                .darken(0.25)
                .fade(valuePosition)
                .rgb()
                .string();
            },
            data: nation.map((feature: any) => ({
              feature,
              value: findValue(feature)
            }))
          }
        ]
      },
      options: {
        showOutline: false,
        showGraticule: false,
        legend: {
          display: false
        },
        tooltips: {
          mode: "single",
          backgroundColor: "white",
          bodyFontFamily: "Gilroy",
          bodyFontColor: "#333333",
          displayColors: false,
          callbacks: {
            label: (data: any) => {
              const translation = translations.find(
                (t: any) => t.name.toLowerCase() === labels[data.index].toLowerCase()
              );

              if (translation) {
                return [translation.translation, data.value];
              }

              return [];
            }
          }
        },
        // responsive: true,
        // ! Only one scale is supported via the options.scale option
        scale: {
          projection: "equalEarth" // ... projection method
        },
        geo: {
          colorScale: {
            display: false
          }
        }
      }
    } as any);
  }

  beforeDestroy() {
    if (!this.chart) {
      return;
    }
    this.canvas.width = 0;
    this.canvas.height = 0;
    this.chart.width = 0;
    this.chart.height = 0;
    this.chart.clear();
    this.chart.destroy();
  }
}
